import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  ChassisHeightFilterValue,
  ChassisHeightFilterOption,
  ChassisHeightForm,
  ChassisHeightFormDropDownKey,
  ChassisHeightListMeta,
  ChassisHeightRow,
  ChassisHeightDetailPayload,
  ChassisHeightUpdatePayload,
  ChassisHeightDeletePayload,
  ChassisHeightYearFilter,
} from "../types";
import {
  initialChassisHeightForm,
  initialFormDropDownState,
  initialState,
} from "./initialState";

export const sliceKey = "chassisHeightState";

type ChassisHeightListSuccessPayload = {
  data: ChassisHeightRow[];
  meta: ChassisHeightListMeta;
};

type FormDropDownSuccessPayload = {
  key: ChassisHeightFormDropDownKey;
  data: ChassisHeightFilterOption[];
  meta: ChassisHeightListMeta;
};

const chassisHeightSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getChassisHeightList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getChassisHeightListSuccess(
      state,
      action: PayloadAction<ChassisHeightListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getChassisHeightListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<ChassisHeightListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.manufacturerFilter = action.payload;
    },

    setSuspensionTypeFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.suspensionTypeFilter = action.payload;
    },

    setMasterModelFilter(
      state,
      action: PayloadAction<ChassisHeightFilterValue>
    ) {
      state.masterModelFilter = action.payload;
    },

    setYearFilter(state, action: PayloadAction<ChassisHeightYearFilter>) {
      state.yearFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialChassisHeightForm;
      state.formDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        masterModels: { ...initialFormDropDownState },
      };
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof ChassisHeightForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialChassisHeightForm;
    },

    getFormDropDownList(
      state,
      action: PayloadAction<ChassisHeightFormDropDownKey>,
    ) {
      const key = action.payload;
      state.formDropDowns[key] = {
        ...initialFormDropDownState,
        perPage: state.formDropDowns[key].perPage,
        loading: true,
      };
    },

    getFormDropDownListSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      const { key, data, meta } = action.payload;
      state.formDropDowns[key] = {
        ...state.formDropDowns[key],
        items: data,
        meta,
        page: meta.currentPage,
        loading: false,
      };
    },

    getFormDropDownListError(
      state,
      action: PayloadAction<{
        key: ChassisHeightFormDropDownKey;
        message: string;
      }>,
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    getFormDropDownNextPage(
      state,
      action: PayloadAction<ChassisHeightFormDropDownKey>,
    ) {
      const dropDown = state.formDropDowns[action.payload];
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getFormDropDownNextPageSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      const { key, data, meta } = action.payload;
      const dropDown = state.formDropDowns[key];
      dropDown.loading = false;
      dropDown.items = dropDown.items.concat(data);
      dropDown.meta = meta;
      dropDown.page = meta.currentPage;
    },

    getFormDropDownNextPageError(
      state,
      action: PayloadAction<{
        key: ChassisHeightFormDropDownKey;
        message: string;
      }>,
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    addChassisHeight(state) {
      state.saving = true;
      state.error = null;
    },

    addChassisHeightSuccess(state, action: PayloadAction<ChassisHeightRow>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialChassisHeightForm;
      state.formDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        masterModels: { ...initialFormDropDownState },
      };
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addChassisHeightError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateChassisHeight(
      state,
      _action: PayloadAction<ChassisHeightUpdatePayload>
    ) {
      state.saving = true;
      state.error = null;
    },

    updateChassisHeightSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateChassisHeightError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteChassisHeight(
      state,
      _action: PayloadAction<ChassisHeightDeletePayload>
    ) {
      state.saving = true;
      state.error = null;
    },

    deleteChassisHeightSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    deleteChassisHeightError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    getChassisHeightDetail(
      state,
      _action: PayloadAction<ChassisHeightDetailPayload>
    ) {
      state.detailLoading = true;
      state.detailError = null;
      state.detail = null;
    },

    getChassisHeightDetailSuccess(
      state,
      action: PayloadAction<ChassisHeightRow>
    ) {
      state.detailLoading = false;
      state.detail = action.payload;
      state.detailError = null;
    },

    getChassisHeightDetailError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },

    clearChassisHeightDetail(state) {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
});

export const { actions: chassisHeightActions, reducer } = chassisHeightSlice;
